import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { BaseModalComponent } from '@app/shared/modals/base-modal/base-modal.component';
import { NgxSmartModalComponent, NgxSmartModalService } from '@bssh/comp-lib';
import { IMultiSelectOptionsModalInput } from '@app/shared/modals/model/action-modal';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { GroupedOption } from '@app/run-planning/model/option';
import { map, tap } from 'rxjs/operators';
import { IMultiSelectFieldSettings } from '@app/shared/forms/multi-select/multi-select.component';
import { MultiSelectFieldValidator } from '@app/shared/forms/multi-select/multi-select.validator';

@Component({
  selector: 'app-multi-select-options-modal',
  templateUrl: './multi-select-options-modal.component.html',
  styleUrls: ['./multi-select-options-modal.component.scss']
})
export class MultiSelectOptionsModalComponent extends BaseModalComponent implements OnInit, AfterViewInit, OnDestroy {

  modalType = 'multiSelectOptionsModal';
  modalTitle = 'Select Option(s)';
  confirmButtonText = 'Select';
  selectionLimitErrorMessage: string = undefined;

  valueProperty = 'value';
  labelProperty = 'text';
  groupedOptions: GroupedOption[];
  groupedOptionsToShow: GroupedOption[];
  settings: IMultiSelectFieldSettings = {};
  isRequired = false;

  localFormGroup: FormGroup;
  filterFormControl: FormControl;

  constructor(
    public ngxSmartModalService: NgxSmartModalService,
    public formBuilder: FormBuilder,
  ) {
    super(ngxSmartModalService);
    this.filterFormControl = this.formBuilder.control('');
    this.localFormGroup = this.formBuilder.group({});
  }

  ngOnInit() {
    super.ngOnInit();

    this.subs.sink = this.data.subscribe({
      next: (modalInput: IMultiSelectOptionsModalInput) => {
        this.valueProperty = modalInput.valueProperty || this.valueProperty;
        this.labelProperty = modalInput.labelProperty || this.labelProperty;
        this.groupedOptions = modalInput.groupedOptions;
        this.groupedOptionsToShow = this.groupedOptions;
        this.settings = modalInput.settings;
        this.isRequired = modalInput.isRequired;
        this.localFormGroup = this.generateFormGroupFromGroupedOptions(this.groupedOptions, modalInput.selectedValues);
      },
      error: console.error,
    });

    this.subs.sink = this.localFormGroup.valueChanges.pipe(
      // Flatten grouped values to a single array of values
      map(this.flattenValues),
      tap(selectedValues => {
        this.setErrorMessage(selectedValues);
      }),
    ).subscribe({
      next: (selectedValues) => {
        this.confirm.emit({ selectedValues });
      },
      error: console.error,
    });
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    this.subs.sink = this.filterFormControl.valueChanges.subscribe({
      next: (query) => {
        const normalizedQuery = query.trim().toLowerCase();
        if (normalizedQuery) {
          this.groupedOptionsToShow = this.groupedOptions.map(group => ({
            ...group,
            options: group.options.filter(option => option.text.toLowerCase().includes(normalizedQuery)),
          }));
        } else {
          this.groupedOptionsToShow = this.groupedOptions;
        }
      },
      error: console.error,
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  getFormControl(groupName: string): FormControl {
    return this.localFormGroup.get(groupName) as FormControl;
  }

  close(modal: NgxSmartModalComponent) {
    this.confirm.emit({ selectedValues: this.flattenValues(this.localFormGroup.value) });
    this.confirm.complete();
    super.close(modal);
  }

  addNewOption() {
    this.confirm.emit({ addNewOption: true });
    this.confirm.complete();
    this.modal.close();
  }

  private generateFormGroupFromGroupedOptions(groupedOptions: GroupedOption[], selectedValues: string[]) {
    const formGroupInput = groupedOptions.map((group) => {
      const optionValues = new Set(group.options.map(o => o.value));
      const currSelection = selectedValues.filter(v => optionValues.has(v));
      return { [group.groupName]: [currSelection] };
    }).reduce((acc, curr) => ({ ...acc, ...curr }), {});
    return this.formBuilder.group(formGroupInput);
  }

  private flattenValues(changes: { [k: string]: string[] }) {
    return Object.values(changes).reduce((acc, curr) => [...acc, ...curr], [] as string[]);
  }

  private setErrorMessage(values: string[]) {
    this.selectionLimitErrorMessage = MultiSelectFieldValidator.withContext({
      isRequired: this.isRequired,
      minSelection: this.settings.minSelection,
      maxSelection: this.settings.maxSelection,
    }).validate(values);
  }

}
